import SettingsIcon from "@mui/icons-material/Settings";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ODOMETER_DECIMAL_OPTIONS, OdometerDecimals, useOdometerDecimals } from "@/hooks/userState/useOdometerDecimals";

/**
 * Gear button to choose the decimals of the odometer, remembered per user.
 *
 * @param children - Further settings of the page, shown under the decimals.
 */
export function OdometerDecimalsButton({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  const { decimals, setDecimals } = useOdometerDecimals();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const title = t("odometerSettings.title");

  return (
    <>
      <Tooltip title={title}>
        <IconButton size="small" aria-label={title} onClick={event => setAnchor(event.currentTarget)}>
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { p: 2 }, role: "dialog", "aria-label": title } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("odometerSettings.decimals")}
        </Typography>
        <RadioGroup
          row
          value={String(decimals)}
          onChange={event => setDecimals(Number(event.target.value) as OdometerDecimals)}
        >
          {ODOMETER_DECIMAL_OPTIONS.map(option => (
            <FormControlLabel key={option} value={String(option)} control={<Radio size="small" />} label={option} />
          ))}
        </RadioGroup>
        {children}
      </Popover>
    </>
  );
}
